import { Select } from 'antd';
import type { PostTypeDto } from '@pams/shared';
import { ReorderableList } from '@/components/ReorderableList';
import { NOT_AVAILABLE } from '@/lib/selectOptions';

/**
 * The post-column picker: an ADDER plus a reorderable list.
 *
 * A controlled component in AntD's custom-control contract — `Form.Item` injects `value`
 * and `onChange` — which is what keeps the form the single store, so a reorder
 * participates in dirty tracking, validation and the live preview for free.
 *
 * ⚠️ THE ARRAY ORDER IS THE DISPLAY ORDER, and the picker is an adder rather than a tag
 * multi-select. AntD's `mode="multiple"` renders its selections as tags INSIDE the
 * control, so with an ordered list beneath it you get the same labels twice — redundant
 * when the two agree and actively confusing when they do not. It also lets the post
 * picker and the command-role editor share one interaction idiom, which on a screen a
 * layman uses twice a year is worth more than the two lines it costs.
 */
export const PostTypeOrderField = ({
  value,
  onChange,
  postTypes,
  resolve,
  disabled,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  postTypes: readonly PostTypeDto[];
  /**
   * ⚠️ [D4.5 §1b] WHAT AN ALREADY-CHOSEN id RENDERS AS — archived rows included.
   * `postTypes` is what may be ADDED and stays active-only; the page merges the master with
   * the saved version's own embedded summaries to build this (`knownPostTypes`).
   */
  resolve?: (id: string) => { name: string; shortName?: string } | undefined;
  disabled?: boolean;
}) => {
  const selected = value ?? [];
  const byId = new Map(postTypes.map((postType) => [postType.id, postType]));

  /**
   * ⚠️ [D4.5 §1b] AN UNRESOLVED ID IS SHOWN AS MISSING, NOT DROPPED.
   *
   * This was a `flatMap` returning `[]` for an id `byId` did not carry — the same root cause
   * as an ObjectId rendering inside a `Select`, wearing the opposite symptom. `/post-types`
   * returns ACTIVE rows only (MEASURED at FIX-1: six exist, five come back), so a draft
   * naming a since-archived post type silently lost a row from its own ordered list. The
   * count fell, the order shifted, nothing said why — and a reorder then wrote the shortened
   * list back, which is the part that loses data rather than merely displaying it wrongly.
   *
   * ⚠️ IT IS STILL NOT RE-OFFERED. The adder above lists `postTypes` and is untouched; this
   * renders only what is already stored. That is `lib/policyColumns.ts`'s rule — filter at
   * USE, never edit the record — applied one level down.
   */
  const items = selected.map((id) => {
    const postType = byId.get(id);
    if (postType) return { id, label: postType.shortName || postType.name, content: postType.name };
    const known = resolve?.(id);
    return known
      ? { id, label: known.shortName || known.name, content: known.name }
      : { id, label: NOT_AVAILABLE, content: NOT_AVAILABLE };
  });

  return (
    <div className="flex flex-col gap-2">
      {/*
        ─────────────────────────────────────────────────────────────────────────────────
        ⚠️ [C3c §11b] `value={null}`, NEVER `undefined` — AND `setPending(undefined)` NEVER DID
        WHAT THE COMMENT HERE USED TO CLAIM.

        The claim was that resetting after each add made the control read "Add a post type"
        again rather than showing the last thing chosen. It did not, and the mechanism is
        MEASURED in the installed sources:

          · `pending` was initialised `undefined` and set to `undefined`, so the `value` prop
            never TRANSITIONED — React bailed out of the state write entirely;
          · `rc-util/es/hooks/useMergedState.js` treats `undefined` as UNCONTROLLED
            (`hasValue` is `value !== undefined`; `mergedValue = value !== undefined ? value :
            innerValue`), so the Select fell through to its own `innerValue`;
          · `rc-select`'s `triggerChange` sets `innerValue` unconditionally on every selection.

        Net: the adder kept displaying the post type just chosen, DIRECTLY ABOVE the reorderable
        list that already shows it. And rc-util's own control-to-uncontrol sync effect could not
        save it either, because that effect depends on `[value]` — which never changed.

        ⚠️ THE SEARCH TEXT WAS NEVER THE PROBLEM. rc-select clears it on select in single mode
        already (`autoClearSearchValue` defaults true); it was the VALUE that stuck.

        `null` is a real value to `useMergedState`, so the control is permanently pinned at
        "nothing chosen" — and rc-select renders a lone `null` as the PLACEHOLDER by name
        (ant-design#25057). `onChange` still fires on every add, including a repeat.
        ─────────────────────────────────────────────────────────────────────────────────
      */}
      <Select
        value={null}
        onChange={(next: string | undefined) => {
          if (next && !selected.includes(next)) onChange?.([...selected, next]);
        }}
        disabled={disabled}
        showSearch
        optionFilterProp="label"
        placeholder="Add a post type"
        className="w-full"
        data-testid="post-type-adder"
        options={postTypes.map((postType) => ({
          value: postType.id,
          label: `${postType.name} (${postType.shortName})`,
          disabled: selected.includes(postType.id),
        }))}
      />

      <ReorderableList
        items={items}
        disabled={disabled ?? false}
        ariaLabel="Post columns"
        emptyText="No post columns yet. Add one above."
        data-testid="post-type-order"
        onReorder={(next) => onChange?.(next.map((item) => item.id))}
        onRemove={(item) => onChange?.(selected.filter((id) => id !== item.id))}
      />
    </div>
  );
};
